import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { Skeleton } from "./ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Label } from "./ui/label";
import { Button } from "./ui/button";
import { Tabs, TabsList, TabsTrigger } from "./ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog";
import {
  ArrowUpCircle, ArrowDownCircle, Wallet, Landmark, UserRound, Grid3x3,
  ChevronLeft, ChevronRight, Printer, Loader2, Clock, CalendarDays,
} from "lucide-react";

const AYLAR = ["Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran", "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık"];
const sayi = new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 0 });
const fmt = (v) => (!v || Math.abs(v) < 0.005 ? "—" : sayi.format(v));

const simdi = () => {
  const t = new Date().toLocaleDateString("sv-SE", { timeZone: "Europe/Istanbul" });
  return { yil: t.slice(0, 4), ay: String(parseInt(t.slice(5, 7))), tarih: t };
};

// Verilen tarihin haftasının Pazartesi'sini döndürür (+offset hafta)
const haftaBaslangici = (tarihStr, offsetHafta = 0) => {
  const d = new Date(tarihStr + "T12:00:00");
  const gun = (d.getDay() + 6) % 7; // Pazartesi=0
  d.setDate(d.getDate() - gun + offsetHafta * 7);
  return d;
};
const isoGun = (d) => d.toISOString().slice(0, 10);

const Hucre = ({ v, bugun, negKirmizi, kalin, onClick }) => (
  <td
    className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-xs ${bugun ? "bg-amber-50" : ""} ${kalin ? "font-bold" : ""} ${negKirmizi && v < 0 ? "text-red-600" : ""} ${!v || Math.abs(v) < 0.005 ? "text-muted-foreground/40" : onClick ? "cursor-pointer hover:bg-sky-100 hover:underline" : ""}`}
    onClick={v && Math.abs(v) >= 0.005 && onClick ? onClick : undefined}
  >
    {fmt(v)}
  </td>
);

const Satir = ({ satir, gunler, tip = "duz", ikon = null, onGunClick }) => {
  const stil =
    tip === "toplam-giris" ? "bg-emerald-50/70 font-bold text-emerald-800"
    : tip === "toplam-cikis" ? "bg-red-50/70 font-bold text-red-800"
    : tip === "net" ? "bg-slate-100 font-bold"
    : tip === "bakiye" ? "bg-sky-50/50 font-semibold"
    : tip === "soluk" ? "text-muted-foreground"
    : "";
  return (
    <tr className={`border-t border-border/40 ${stil}`}>
      <td className="sticky left-0 z-10 bg-inherit px-3 py-1.5 text-xs whitespace-nowrap min-w-[190px] backdrop-blur bg-card/95">
        <span className="inline-flex items-center gap-1.5">{ikon}{satir.etiket}</span>
      </td>
      <td className={`px-2 py-1.5 text-right tabular-nums whitespace-nowrap text-xs font-semibold border-r ${satir.toplam < 0 ? "text-red-600" : ""}`}>
        {tip === "bakiye" ? fmt(satir.degerler[satir.degerler.length - 1]) : fmt(satir.toplam)}
      </td>
      {satir.degerler.map((v, i) => (
        <Hucre
          key={i} v={v} bugun={gunler[i]?.bugun_mu}
          negKirmizi={tip === "net" || tip === "bakiye"}
          kalin={tip.startsWith("toplam") || tip === "net"}
          onClick={tip !== "bakiye" && onGunClick ? () => onGunClick(gunler[i].tarih) : undefined}
        />
      ))}
    </tr>
  );
};

const BolumBaslik = ({ ad, renk, gunSayisi }) => (
  <tr>
    <td className={`sticky left-0 z-10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide ${renk} bg-card/95 backdrop-blur`}>{ad}</td>
    <td className="border-r"></td>
    <td colSpan={gunSayisi}></td>
  </tr>
);

/* ---- Gün detay penceresi ---- */
const DETAY_BOLUMLER = [
  { key: "tahsilatlar", baslik: "Tahsilatlar", renk: "text-emerald-700", toplamKey: "tahsilat", icon: ArrowUpCircle },
  { key: "giderler", baslik: "Giderler", renk: "text-red-700", toplamKey: "gider", icon: ArrowDownCircle },
  { key: "odemeler", baslik: "Ödemeler", renk: "text-red-700", toplamKey: "odeme", icon: ArrowDownCircle },
  { key: "beklenen", baslik: "Beklenen Tahsilatlar (Vade)", renk: "text-amber-700", toplamKey: "beklenen", icon: Clock },
  { key: "planlanan", baslik: "Planlanan Bütçe Ödemeleri", renk: "text-blue-700", toplamKey: "planlanan", icon: CalendarDays },
];

const GunDetayDialog = ({ tarih, onClose }) => {
  const [detay, setDetay] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);

  useEffect(() => {
    if (!tarih) return;
    setDetay(null);
    setYukleniyor(true);
    api.get("/nakit-akis/gun-detay", { params: { tarih } })
      .then((r) => setDetay(r.data))
      .catch((err) => toast.error(getErrMsg(err, "Gün detayı yüklenemedi.")))
      .finally(() => setYukleniyor(false));
  }, [tarih]);

  return (
    <Dialog open={!!tarih} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-card" data-testid="matris-gun-detay-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            {tarih && formatDate(tarih)} {detay?.gun_adi ? `• ${detay.gun_adi}` : ""} — İşlem Dökümü
          </DialogTitle>
          <DialogDescription>Bu güne ait tüm giriş/çıkış hareketleri</DialogDescription>
        </DialogHeader>
        {yukleniyor ? (
          <div className="flex items-center justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : detay ? (
          <div className="space-y-4">
            {DETAY_BOLUMLER.map(({ key, baslik, renk, toplamKey, icon: Icon }) => {
              const liste = detay[key] || [];
              if (liste.length === 0) return null;
              return (
                <div key={key} className="rounded-lg border" data-testid={`gun-detay-${key}`}>
                  <div className={`flex items-center justify-between px-3 py-2 border-b bg-muted/30 text-xs font-semibold ${renk}`}>
                    <span className="inline-flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" />{baslik} ({liste.length})</span>
                    <span className="tabular-nums">{formatTL(detay.toplamlar[toplamKey])}</span>
                  </div>
                  <div className="divide-y divide-border/40">
                    {liste.map((h, i) => (
                      <div key={i} className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{h.ad}</p>
                          <p className="text-muted-foreground truncate">
                            {[h.no, h.odeme_yeri, h.hesap, h.alan_kisi && `Alan: ${h.alan_kisi}`, h.kategori].filter(Boolean).join(" • ") || "—"}
                          </p>
                        </div>
                        <span className="font-semibold tabular-nums whitespace-nowrap">{formatTL(h.tutar)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {DETAY_BOLUMLER.every(({ key }) => (detay[key] || []).length === 0) && (
              <p className="text-sm text-muted-foreground text-center py-6" data-testid="gun-detay-bos">Bu günde işlem yok.</p>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export const NakitMatris = () => {
  const ilk = simdi();
  const [mod, setMod] = useState("hafta"); // hafta | ay
  const [haftaOffset, setHaftaOffset] = useState(0);
  const [yil, setYil] = useState(ilk.yil);
  const [ay, setAy] = useState(ilk.ay);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detayTarih, setDetayTarih] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (mod === "hafta") {
        const bas = haftaBaslangici(simdi().tarih, haftaOffset);
        const son = new Date(bas);
        son.setDate(son.getDate() + 6);
        params = { baslangic: isoGun(bas), bitis: isoGun(son) };
      } else {
        params = { yil, ay };
      }
      const res = await api.get("/nakit-akis/matris", { params });
      setData(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Nakit matrisi yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [mod, haftaOffset, yil, ay]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading && !data) return <Skeleton className="h-96 w-full" data-testid="nakit-matris-loading" />;
  if (!data) return null;

  const g = data.gunler;
  const donemEtiket = mod === "hafta"
    ? `${g[0].gun} ${AYLAR[parseInt(g[0].tarih.slice(5, 7)) - 1]} – ${g[g.length - 1].gun} ${AYLAR[parseInt(g[g.length - 1].tarih.slice(5, 7)) - 1]} ${g[g.length - 1].tarih.slice(0, 4)}`
    : `${AYLAR[parseInt(ay) - 1]} ${yil}`;

  return (
    <div className="space-y-3" data-testid="nakit-matris">
      <style>{`
        @media print {
          @page { size: A4 landscape; margin: 8mm; }
          body * { visibility: hidden !important; }
          #nakit-matris-yazdir, #nakit-matris-yazdir * { visibility: visible !important; }
          #nakit-matris-yazdir { position: absolute; inset: 0; background: white; overflow: visible !important; border: none !important; }
          #nakit-matris-yazdir table { font-size: 9px !important; width: 100%; }
          #nakit-matris-yazdir td, #nakit-matris-yazdir th { padding: 2px 4px !important; }
        }
      `}</style>
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <div className="flex items-center gap-2">
          <Grid3x3 className="h-4 w-4 text-[hsl(var(--primary))]" />
          <div>
            <h3 className="font-display text-sm font-semibold">Nakit Matrisi — Günler Yatay</h3>
            <p className="text-[11px] text-muted-foreground">Tahsilatlar ve ödemeler alt alta • hücreye tıklayın: gün dökümü • gün sonu Kasa / Banka / Yönetim bakiyeleri</p>
          </div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          <Tabs value={mod} onValueChange={(v) => { setMod(v); setHaftaOffset(0); }}>
            <TabsList className="h-10 bg-muted/60 p-1">
              <TabsTrigger value="hafta" className="h-8 px-3 text-xs" data-testid="matris-mod-hafta">Haftalık</TabsTrigger>
              <TabsTrigger value="ay" className="h-8 px-3 text-xs" data-testid="matris-mod-ay">Aylık</TabsTrigger>
            </TabsList>
          </Tabs>
          {mod === "hafta" ? (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setHaftaOffset((o) => o - 1)} data-testid="matris-hafta-geri" aria-label="Önceki hafta">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button variant="outline" className="h-10 px-3 text-xs" onClick={() => setHaftaOffset(0)} disabled={haftaOffset === 0} data-testid="matris-hafta-bugun">
                Bu Hafta
              </Button>
              <Button variant="outline" size="icon" className="h-10 w-10" onClick={() => setHaftaOffset((o) => o + 1)} data-testid="matris-hafta-ileri" aria-label="Sonraki hafta">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <>
              <Label className="text-xs text-muted-foreground">Dönem</Label>
              <Select value={ay} onValueChange={setAy}>
                <SelectTrigger className="h-10 w-32" data-testid="matris-ay-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  {AYLAR.map((a, i) => <SelectItem key={a} value={String(i + 1)}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={yil} onValueChange={setYil}>
                <SelectTrigger className="h-10 w-24" data-testid="matris-yil-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card">
                  {["2025", "2026", "2027"].map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </>
          )}
          <Button variant="outline" onClick={() => window.print()} className="h-10 gap-1.5" data-testid="matris-yazdir-button">
            <Printer className="h-4 w-4" />
            Yazdır / PDF
          </Button>
        </div>
      </div>

      {mod === "hafta" && (
        <p className="text-xs text-muted-foreground print:hidden" data-testid="matris-hafta-aralik">
          Hafta: <strong className="text-foreground">{donemEtiket}</strong>{haftaOffset === 0 ? " (bu hafta)" : ""}
        </p>
      )}

      <div id="nakit-matris-yazdir" className="rounded-xl border bg-card overflow-x-auto" data-testid="nakit-matris-tablo">
        <div className="hidden print:flex items-baseline justify-between px-3 pt-2 pb-1 border-b">
          <span className="font-bold text-sm">3A Otomotiv — Nakit Matrisi</span>
          <span className="text-xs">{donemEtiket} • Rapor: {formatDate(data.bugun)}</span>
        </div>
        <table className="text-sm border-collapse min-w-max">
          <thead>
            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
              <th className="sticky left-0 z-20 bg-card px-3 py-2 text-left min-w-[190px]">Kalem</th>
              <th className="px-2 py-2 text-right border-r">Toplam</th>
              {g.map((d) => (
                <th
                  key={d.tarih}
                  className={`px-2 py-2 text-center min-w-[64px] cursor-pointer hover:bg-sky-50 ${d.bugun_mu ? "bg-amber-100 text-amber-900 rounded-t" : ""}`}
                  onClick={() => setDetayTarih(d.tarih)}
                  data-testid={`matris-gun-baslik-${d.tarih}`}
                >
                  <div className="font-bold text-xs">{d.gun}</div>
                  <div className="font-normal">{d.gun_adi.slice(0, 3)}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <BolumBaslik ad="Tahsilatlar (Giriş)" renk="text-emerald-700" gunSayisi={g.length} />
            {data.tahsilatlar.satirlar.map((s) => (
              <Satir key={s.etiket} satir={s} gunler={g} onGunClick={setDetayTarih} ikon={
                s.etiket === "Kasa" ? <Wallet className="h-3 w-3 text-emerald-600" />
                : s.etiket === "Banka" ? <Landmark className="h-3 w-3 text-emerald-600" />
                : <UserRound className="h-3 w-3 text-emerald-600" />
              } />
            ))}
            <Satir satir={data.tahsilatlar.toplam} gunler={g} tip="toplam-giris" onGunClick={setDetayTarih} ikon={<ArrowUpCircle className="h-3.5 w-3.5" />} />
            <Satir satir={data.tahsilatlar.beklenen} gunler={g} tip="soluk" onGunClick={setDetayTarih} />

            <BolumBaslik ad="Ödemeler / Çıkışlar" renk="text-red-700" gunSayisi={g.length} />
            {data.cikislar.gider_satirlar.map((s) => <Satir key={s.etiket} satir={s} gunler={g} onGunClick={setDetayTarih} />)}
            {data.cikislar.odeme_satirlar.map((s) => <Satir key={s.etiket} satir={s} gunler={g} onGunClick={setDetayTarih} />)}
            {data.cikislar.gider_satirlar.length === 0 && data.cikislar.odeme_satirlar.length === 0 && (
              <tr className="border-t border-border/40">
                <td className="sticky left-0 z-10 bg-card/95 px-3 py-1.5 text-xs text-muted-foreground" colSpan={2}>Bu dönemde çıkış yok</td>
                <td colSpan={g.length}></td>
              </tr>
            )}
            <Satir satir={data.cikislar.toplam} gunler={g} tip="toplam-cikis" onGunClick={setDetayTarih} ikon={<ArrowDownCircle className="h-3.5 w-3.5" />} />
            <Satir satir={data.cikislar.planlanan} gunler={g} tip="soluk" onGunClick={setDetayTarih} />

            <BolumBaslik ad="Mevcut Durum (Gün Sonu)" renk="text-sky-700" gunSayisi={g.length} />
            <Satir satir={data.durum.net} gunler={g} tip="net" onGunClick={setDetayTarih} />
            <Satir satir={data.durum.kasa} gunler={g} tip="bakiye" ikon={<Wallet className="h-3 w-3 text-sky-600" />} />
            <Satir satir={data.durum.banka} gunler={g} tip="bakiye" ikon={<Landmark className="h-3 w-3 text-sky-600" />} />
            <Satir satir={data.durum.yonetim} gunler={g} tip="bakiye" ikon={<UserRound className="h-3 w-3 text-sky-600" />} />
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-1 text-[11px] text-muted-foreground print:hidden" data-testid="nakit-matris-devirler">
        <span>Dönem başı devirleri — Kasa: <strong className="text-foreground tabular-nums">{formatTL(data.durum.kasa.devir)}</strong></span>
        <span>Banka: <strong className="text-foreground tabular-nums">{formatTL(data.durum.banka.devir)}</strong></span>
        <span>Yönetim: <strong className="text-foreground tabular-nums">{formatTL(data.durum.yonetim.devir)}</strong></span>
        <span className="w-full sm:w-auto">"Toplam" sütunu bakiye satırlarında dönem sonu değerini gösterir. Sarı sütun bugündür.</span>
      </div>

      <GunDetayDialog tarih={detayTarih} onClose={() => setDetayTarih(null)} />
    </div>
  );
};
